import {
  OpenUrlTarget,
  openURL,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {
  CommonActions,
  NavigationContainerRef,
  useNavigation,
} from '@react-navigation/native';
import Cookies from 'js-cookie';
import React, {MutableRefObject, useLayoutEffect} from 'react';
import {Platform} from 'react-native';

import {APP_BASE_URL, LinkData, SupportedLinksMap} from 'models/Navigation';

// Navigation service to navigate from sagas, etc.
// https://reactnavigation.org/docs/navigating-without-navigation-prop

export const navigationRef = React.createRef<NavigationContainerRef>();
export const navigationReadyRef: MutableRefObject<boolean | null> =
  React.createRef();
const isWeb = Platform.OS === 'web';

export const navigate = (name: any, params?: any, retries = 0) => {
  if (!navigationReadyRef.current || !navigationRef.current) {
    /**
     * Navigation container may not be ready before this fn is called.
     * Allow upto 100ms = 5 * 20ms for the container/navigator to be ready.
     * https://reactnavigation.org/docs/5.x/navigating-without-navigation-prop/
     */
    if (retries < 5) {
      setTimeout(() => navigate(name, params, retries + 1), 20);
    }
    return;
  }

  navigationRef.current?.navigate(name, params);
};

export const getRouteParams = () => {
  if (!navigationReadyRef.current || !navigationRef.current) {
    return;
  }

  return navigationRef?.current?.getCurrentRoute()?.params;
};

export const setRouteParams = (params, retries = 0) => {
  if (!navigationReadyRef.current || !navigationRef.current) {
    /**
     * Navigation container may not be ready before this fn is called.
     * Allow upto 100ms = 5 * 20ms for the container/navigator to be ready.
     * https://reactnavigation.org/docs/5.x/navigating-without-navigation-prop/
     */
    if (retries < 5) {
      setTimeout(() => setRouteParams(params, retries + 1), 20);
    }
    return;
  }
  const routeParams = navigationRef?.current?.getCurrentRoute()?.params;
  navigationRef?.current?.dispatch(
    CommonActions.setParams({...routeParams, ...params})
  );
};

export const goHome = (retries = 0) => {
  if (!navigationReadyRef.current || !navigationRef.current) {
    /**
     * Navigation container may not be ready before this fn is called.
     * Allow upto 100ms = 5 * 20ms for the container/navigator to be ready.
     * https://reactnavigation.org/docs/5.x/navigating-without-navigation-prop/
     */
    if (retries < 5) {
      setTimeout(() => goHome(retries + 1), 20);
    }
    return;
  }

  try {
    navigationRef.current?.navigate('Home');
  } catch (e) {}
};

export const goBack = (retries = 0) => {
  if (
    !navigationReadyRef.current ||
    !navigationRef.current ||
    !navigationRef.current?.canGoBack()
  ) {
    /**
     * Navigation container may not be ready before this fn is called.
     * Allow upto 100ms = 5 * 20ms for the container/navigator to be ready.
     * https://reactnavigation.org/docs/5.x/navigating-without-navigation-prop/
     */
    if (retries < 5) {
      setTimeout(() => goBack(retries + 1), 20);
    }
    return;
  }

  if (navigationRef.current?.canGoBack()) {
    try {
      navigationRef.current?.goBack();
    } catch (e) {
      navigationRef.current?.navigate('home');
    }
  }
};

/**
 * Custom hook to replace the navigation headerLeft property. setOptions is not exposed in the
 * navigationRef so we can't use functions like above
 * Pass in a react element to replace with that element
 * Pass in null to remove the header element
 * Pass in false to skip (leave the current element as-is)
 */
export const useReplaceHeaderLeft = (
  contents: React.ReactElement | null | false
) => {
  const navigation = useNavigation();

  useLayoutEffect(() => {
    if (contents === false) {
      return;
    }

    navigation.setOptions({
      headerLeft: () => contents,
    });
  }, [navigation, contents]);
};

// These card styles are needed on all screens in order for ScrollViews and
// FlatLists to be scrollable on iOS 12.
export const navigationCardStyle = {
  cardStyle: {flex: 1},
};

/**
 * Returns link data conditionally based on whether or not we are in Legacy mode
 * and whether or not the link is a supported New Rewards screen
 */
export const getLinkData = (title: string, url: string): LinkData => {
  const newRewardsScreen = SupportedLinksMap.get(title);
  return newRewardsScreen && !window.isLegacyMyRewards
    ? {
        title,
        newRewardsScreen,
        url,
        analyticsHref: `/${APP_BASE_URL}/${newRewardsScreen}`,
      }
    : {
        title,
        url,
        analyticsHref: url,
      };
};

export interface NavigateOrOpenUrlOptions {
  legacyUrl: string;
  newRewardsScreen: string | undefined;
  target?: OpenUrlTarget;
}

export const navigateOrOpenUrl = ({
  legacyUrl,
  newRewardsScreen,
  target,
}: NavigateOrOpenUrlOptions) => {
  if (newRewardsScreen && !isWeb) {
    navigate(newRewardsScreen);
  } else {
    openURL(legacyUrl, target);
  }
};

// Called when user is logged in
export function getLogin() {
  try {
    if (isWeb) {
      Cookies.remove('mfa_token');
      goBack();
    } else {
      goHome();
    }
  } catch (err) {}
}
